import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

import Dashboard from "./ProfilView/Dashboard";
import MyTeam from "./ProfilView/MyTeam";
import Ranking from "./ProfilView/Ranking";
import MyRaces from "./ProfilView/MyRaces";
import MyProfile from "./ProfilView/MyProfile";

import "./styles/Profil.css";

import cible from "../medias/png/icons/cible.png";

import baroudeur from "../medias/png/background/baroudeurs.png";
import courseaetape from "../medias/png/background/courses.png";
import classicman from "../medias/png/background/classicman.png";
import polyvalent from "../medias/png/background/polyvalents.png";
import sprinteur from "../medias/png/background/sprinteurs.png";
import chrono from "../medias/png/background/chrono.png";

import appStoreBadge from "../medias/png/logo/appstore.png";
import playStoreBadge from "../medias/png/logo/playstore.png";


const Profil = () => {
  const { t, i18n } = useTranslation();

  const location = useLocation();
  const navigate = useNavigate();
  const { user, userId } = useAuth();

  const [generalCyclists, setGeneralCyclists] = useState([]);
  const [espoirCyclists, setEspoirCyclists] = useState([]);
  const [giroCyclists, setGiroCyclists] = useState([]);
  const [tdfCyclists, setTdfCyclists] = useState([]);
  const [vueltaCyclists, setVueltaCyclists] = useState([]);
  const [generalRankings, setGeneralRankings] = useState([]);
  const [generalRankings2025, setGeneralRankings2025] = useState([]);
  const [espoirRankings, setEspoirRankings] = useState([]);
  const [giroRankings, setGiroRankings] = useState([]);
  const [tdfRankings, setTdfRankings] = useState([]);
  const [vueltaRankings, setVueltaRankings] = useState([]);
  const [races, setRaces] = useState([]);
  const [emblems, setEmblems] = useState([]);
  const [userLeagues, setUserLeagues] = useState([]);
  const [leagueMembersById, setLeagueMembersById] = useState({});

  const [cyclists, setCyclists] = useState([]);
  const [epopeeLogo, setEpopeeLogo] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const queryParams = new URLSearchParams(location.search);
  const initialSection = queryParams.get("section");
  const [sectionActive, setSectionActive] = useState(initialSection || "Dashboard");

  const [selectedDate] = useState(new Date());
  const [confirmValidDataPopup, setConfirmValidDataPopup] = useState(false);

  const currentLang = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en")
    ? "en"
    : "fr";

  const formatRank = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return t("profil.unranked");

    if (currentLang === "fr") {
      if (num === 1) return "1er";
      return `${num}e`;
    }

    const mod10 = num % 10;
    const mod100 = num % 100;
    if (mod10 === 1 && mod100 !== 11) return `${num}st`;
    if (mod10 === 2 && mod100 !== 12) return `${num}nd`;
    if (mod10 === 3 && mod100 !== 13) return `${num}rd`;
    return `${num}th`;
  };

  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    // setSelectedDate(newDate);
  };

  const showSession = (sectionName) => {
    setSectionActive(sectionName);
  };

  const normalizeSpeciality = (speciality) =>
    String(speciality || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "")
      .toLowerCase();

  useEffect(() => {
    const fetchCyclists = async () => {
      if (!user) {
        navigate("/logIn");
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/cyclistsListUser`, {
          credentials: "include",
        });

        if (!response.ok) {
          setGeneralCyclists([]);
          setEspoirCyclists([]);
          setGiroCyclists([]);
          setTdfCyclists([]);
          setVueltaCyclists([]);
          throw new Error(t("profil.errors.fetchCyclists"));
        }

        const data = await response.json();

        setGeneralCyclists(data.generalCyclists || []);
        setEspoirCyclists(data.espoirCyclists || []);
        setGiroCyclists(data.giroCyclists || []);
        setTdfCyclists(data.tdfCyclists || []);
        setVueltaCyclists(data.vueltaCyclists || []);

        const specialityImages = {
          baroudeur: baroudeur,
          courseaetapes: courseaetape,
          classicman: classicman,
          polyvalent: polyvalent,
          sprinteur: sprinteur,
          contrelamontre: chrono,
        };

        const normalizedSpecialityImages = Object.keys(specialityImages).reduce((acc, key) => {
          acc[key] = specialityImages[key];
          return acc;
        }, {});

        const cyclistSourceForSpeciality = [
          ...(data.generalCyclists || []),
          ...(data.espoirCyclists || []),
          ...(data.giroCyclists || []),
          ...(data.tdfCyclists || []),
          ...(data.vueltaCyclists || []),
        ];

        const specialityCounts = cyclistSourceForSpeciality.reduce((acc, cyclist) => {
          const normalized = normalizeSpeciality(cyclist.speciality);
          acc[normalized] = (acc[normalized] || 0) + 1;
          return acc;
        }, {});

        const specialityKeys = Object.keys(specialityCounts);
        if (specialityKeys.length === 0) return;

        const mostFrequentSpeciality = specialityKeys.reduce((a, b) =>
          specialityCounts[a] >= specialityCounts[b] ? a : b
        );

        setEpopeeLogo(normalizedSpecialityImages[mostFrequentSpeciality]);
      } catch (error) {
        console.error("Erreur:", error);
      }
    };

    fetchCyclists();
  }, [apiUrl, user, navigate, setGeneralCyclists, setEspoirCyclists, t]);

  useEffect(() => {
    const fetchUserLeagues = async () => {
      if (!user) {
        navigate("/logIn");
        return;
      }

      try {
        const res = await fetch(`${apiUrl}/getUserLeague`, {
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(t("profil.errors.fetchLeagues"));
        }

        const data = await res.json();

        const normalizeLeagues = (raw) => {
          if (!Array.isArray(raw)) return [];
          return raw
            .map((m) => ({
              leagueId: Number(m.leagueId),
              leagueName: String(m.leagueName ?? t("profil.leagueFallbackName")),
            }))
            .filter((m) => Number.isFinite(m.leagueId));
        };

        const normalizeMembersMap = (raw) => {
          const out = {};
          if (!raw || typeof raw !== "object") return out;

          Object.entries(raw).forEach(([k, v]) => {
            const leagueId = Number(k);
            if (!Number.isFinite(leagueId)) return;

            if (Array.isArray(v)) {
              out[leagueId] = v.map((x) => Number(x)).filter((n) => Number.isFinite(n));
            } else {
              out[leagueId] = [];
            }
          });

          return out;
        };

        setUserLeagues(normalizeLeagues(data.leagues));
        setLeagueMembersById(normalizeMembersMap(data.membersByLeague));
      } catch (e) {
        console.error(e);
        setUserLeagues([]);
        setLeagueMembersById({});
      }
    };

    fetchUserLeagues();
  }, [apiUrl, user, navigate, t]);

  useEffect(() => {
    const fetchRankings = async () => {
      if (!user) {
        navigate("/logIn");
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/ranking`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(t("profil.errors.fetchRanking"));
        }

        const data = await response.json();

        setGeneralRankings(data.generalRankings);
        setGeneralRankings2025(data.generalRankings2025);
        setEspoirRankings(data.espoirRankings);
        setGiroRankings(data.giroRankings || []);
        setTdfRankings(data.tdfRankings || []);
        setVueltaRankings(data.vueltaRankings || []);
      } catch (error) {
        console.error("Erreur:", error);
      }
    };
    fetchRankings();
  }, [apiUrl, user, navigate, t]);

  useEffect(() => {
    const fetchEmblems = async () => {
      if (!user) {
        navigate("/logIn");
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/getUserEmblems`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(t("profil.errors.fetchEmblems"));
        }

        const data = await response.json();
        setEmblems(data.emblems);
      } catch (error) {
        console.error("Erreur:", error);
      }
    };

    fetchEmblems();
  }, [apiUrl, user, navigate, t]);

  useEffect(() => {
    const fetchCyclistsList = async () => {
      if (!user) {
        navigate("/logIn");
        return;
      }

      try {
        const res = await fetch(`${apiUrl}/cyclists-list/1`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(t("profil.errors.fetchRidersList"));
        }
        const data = await res.json();
        setCyclists(Array.isArray(data) ? data : data?.cyclists || []);
      } catch (e) {
        console.error(e);
        setCyclists([]);
      }
    };

    fetchCyclistsList();
  }, [apiUrl, user, navigate, t]);

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchPersonalRaces = async () => {
      try {
        const response = await fetch(`${apiUrl}/racesListUser`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(t("profil.errors.fetchPersonalRaces"));
        }

        const data = await response.json();
        setRaces(data);
      } catch (error) {
        console.error("Erreur:", error);
      }
    };

    fetchPersonalRaces();
  }, [apiUrl, user, navigate, t]);

  const handleClosePopup = () => {
    setConfirmValidDataPopup(false);
  };

  const groupTitleKeyByDivision = (division) => {
    if (division === "world tour") return "worldTour";
    if (division === "continental") return "continental";
    if (division === "majeur") return "major";
    return "proSeries";
  };

  return (
    <main className="profil">
      {confirmValidDataPopup && (
        <div className="select-races-bonus-popup">
          <div className="select-races-bonus-popup-content">
            <p>{t("profil.popup.resetRankingMessage")}</p>
            <button onClick={handleClosePopup}>{t("profil.popup.close")}</button>
          </div>
        </div>
      )}

      <div className="profil-menu">
        <div className="profil-menu-profil">
          {epopeeLogo && <img src={epopeeLogo} alt={t("profil.alt.epopeeLogo")} />}

          <p>
            {t("profil.currentRankLabel")}{" "}
            {(() => {
              const userRanking = generalRankings.find((ranking) => ranking.userId === userId);
              return userRanking ? formatRank(userRanking.position) : t("profil.unranked");
            })()}
          </p>

          <p>
            {t("profil.rank2025Label")}{" "}
            {generalRankings2025 ? formatRank(generalRankings2025) : t("profil.unranked")}
          </p>

          <p>{t("profil.emblemsCountLabel", { count: emblems.length })}</p>

          <input
            id="date-select"
            type="date"
            value={new Date(selectedDate.getTime() - selectedDate.getTimezoneOffset() * 60000)
              .toISOString()
              .split("T")[0]}
            onChange={handleDateChange}
          />
        </div>

        <div className="profil-menu-right">
          <div className="profil-menu-onglet">
            <h4
              onClick={() => showSession("Dashboard")}
              className={sectionActive === "Dashboard" ? "active" : ""}
            >
              {t("profil.tabs.dashboard")}
            </h4>

            <h4
              onClick={() => showSession("MyTeam")}
              className={sectionActive === "MyTeam" ? "active" : ""}
            >
              {t("profil.tabs.myTeam")}
            </h4>

            <h4
              onClick={() => showSession("Ranking")}
              className={sectionActive === "Ranking" ? "active" : ""}
            >
              {t("profil.tabs.ranking")}
            </h4>

            <h4
              onClick={() => showSession("MyRaces")}
              className={sectionActive === "MyRaces" ? "active" : ""}
            >
              {t("profil.tabs.myCalendar")}
            </h4>

            <h4
              onClick={() => showSession("MyProfile")}
              className={sectionActive === "MyProfile" ? "active" : ""}
            >
              {t("profil.tabs.profile")}
            </h4>
          </div>

          <div className="profil-infos">
            <div className="profil-image-container">
              <img src={cible} alt={t("profil.alt.target")} className="profil-image" />
            </div>

            {["majeur", "continental", "pro series", "world tour"].map((groupId) => {
              const groupRaces = races.filter(
                (race) => race.division === groupId && race.isBonus === 1 && race.teamId === 1
              );

              if (groupRaces.length === 0) return null;

              const groupKey = groupTitleKeyByDivision(groupId);
              const groupTitle = t(`profil.raceGroups.${groupKey}`);

              return (
                <div className="profil-container" key={groupId}>
                  {groupRaces.map((race, index) => (
                    <div className="profil-row" key={index}>
                      <img
                        src={`${process.env.PUBLIC_URL}/flags/${race.country
                          .normalize("NFD")
                          .replace(/[\u0300-\u036f]/g, "")
                          .replace(/ /g, "_")
                          .toLowerCase()}.png`}
                        alt={t("profil.flagAlt", { country: race.country })}
                        width="20"
                        height="20"
                      />
                      <span>{race.name}</span>
                    </div>
                  ))}

                  <div className="profil-row">
                    <span className="profil-badge">{groupTitle}</span>
                  </div>
                </div>
              );
            })}

            {/*<div className="profil-pub">
              <ins
                className="adsbygoogle"
                style={{ display: "inline-block", width: "400px", height: "100px" }}
                data-ad-client="ca-pub-1359392757710017"
                data-ad-slot="1175616414"
              ></ins>
            </div>*/}
            <aside className="profil-stores">
              <p className="profil-stores-title">
                {t("profil.stores.available")}
              </p>

              <div className="profil-stores-badges">
                <a
                  href="https://apps.apple.com/fr/app/epop%C3%A9e-vf/id6756826740"
                  target="_blank"
                  rel="noreferrer"
                  className="profil-store-link"
                  aria-label="Open on the App Store"
                >
                  <img src={appStoreBadge} alt="Download on the App Store" />
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.velofute_app"
                  target="_blank"
                  rel="noreferrer"
                  className="profil-store-link"
                  aria-label="Get it on Google Play"
                >
                  <img src={playStoreBadge} alt="Get it on Google Play" />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div className="profil-content-container">
        {sectionActive === "Dashboard" && (
          <Dashboard
            generalCyclists={generalCyclists}
            espoirCyclists={espoirCyclists}
            giroCyclists={giroCyclists}
            tdfCyclists={tdfCyclists}
            generalRankings={generalRankings}
            espoirRankings={espoirRankings}
            giroRankings={giroRankings}
            tdfRankings={tdfRankings}
            races={races}
            today={selectedDate}
            showSession={showSession}
          />
        )}

        {sectionActive === "MyTeam" && (
          <MyTeam
            generalCyclists={generalCyclists}
            espoirCyclists={espoirCyclists}
            giroCyclists={giroCyclists}
            tdfCyclists={tdfCyclists}
            vueltaCyclists={vueltaCyclists}
          />
        )}

        {sectionActive === "Ranking" && (
          <Ranking
            generalRankings={generalRankings}
            espoirRankings={espoirRankings}
            giroRankings={giroRankings}
            tdfRankings={tdfRankings}
            vueltaRankings={vueltaRankings}
            cyclists={cyclists}
            userLeagues={userLeagues}
            leagueMembersById={leagueMembersById}
          />
        )}

        {sectionActive === "MyRaces" && <MyRaces races={races} today={selectedDate} />}

        {sectionActive === "MyProfile" && (
          <MyProfile emblems={emblems} generalCyclists={generalCyclists} />
        )}
      </div>
    </main>
  );
};

export default Profil;
